import { AlertTriangle, RotateCcw } from "lucide-react"
import { Button } from "./ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog"

interface ReprocessConfirmDialogProps {
  open: boolean
  onConfirm: () => void
  onCancel: () => void
  isProcessing?: boolean
  /** Number of invoices (for bulk reprocess). Defaults to 1. */
  count?: number
  /** Number of child (split) invoices that will also be deleted. */
  childrenCount?: number
}

export const ReprocessConfirmDialog = ({
  open,
  onConfirm,
  onCancel,
  isProcessing = false,
  count = 1,
  childrenCount = 0,
}: ReprocessConfirmDialogProps) => {
  const isBulk = count > 1
  const label = isBulk ? `${count} Invoices` : "Invoice"

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-12 h-12 bg-amber-100 rounded-full">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Reprocess {label}
              </DialogTitle>
              <p className="text-sm text-gray-500 mt-1">
                This action cannot be undone.
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4 space-y-3">
          <p className="text-sm text-gray-700">
            This will delete all extracted data, corrections, currency representations,
            and ERP metadata. {isBulk ? `The ${count} selected invoices` : "The invoice"} will
            be re-processed from the original document{isBulk ? "s" : ""}.
          </p>

          {childrenCount > 0 && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              This document contains {childrenCount} split invoice{childrenCount === 1 ? "" : "s"} which
              will also be deleted and re-extracted.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Reprocessing...
              </>
            ) : (
              <>
                <RotateCcw className="w-4 h-4 mr-2" />
                Reprocess
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
